import React, { useEffect } from 'react';
import {
    useExpanded,
    useFilters,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from 'react-table';
import { Input, Table } from 'reactstrap';
import ReactPaginate from 'react-paginate';
import Loading from '../Loading';
import { isFunction } from '../../utility/helpers/isFunction';

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return (
            <>
                <Input type="checkbox" ref={resolvedRef} {...rest} />
            </>
        );
    },
);

const StickyTable = ({
    columns,
    data,
    renderRowSubComponent,
    handleSearch,
    pageCount: controlledPageCount,
    isSubComponent = true,
    currentPage = 0,
    autoResetExpanded = true,
    isLoading = false,
    disableFilters = true,
    onFilter,
    initialFilters = [],
    wrapperStyles = {},
    formatRowProps,
    enableRowSelection = false,
    setSelectedRows = () => {},
}) => {
    const plugins = [useFilters, useGlobalFilter, useExpanded, usePagination];

    if (enableRowSelection) {
        plugins.push(useRowSelect);
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        visibleColumns,
        pageCount,
        selectedFlatRows,
        state: { filters },
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageIndex: currentPage,
                pageSize: 25,
                filters: initialFilters,
            }, // Pass our hoisted table state
            manualPagination: true,
            pageCount: controlledPageCount,
            autoResetExpanded: autoResetExpanded,
            disableFilters: disableFilters,
            manualFilters: true,
        },
        ...plugins,
        (hooks) => {
            if (enableRowSelection) {
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        id: 'selection',
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ getToggleAllPageRowsSelectedProps }) => {
                            return (
                                <div>
                                    <IndeterminateCheckbox
                                        {...getToggleAllPageRowsSelectedProps()}
                                    />
                                </div>
                            );
                        },
                        // The cell can use the individual row's getToggleRowSelectedProps method
                        // to the render a checkbox
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox
                                    {...row.getToggleRowSelectedProps()}
                                />
                            </div>
                        ),
                    },
                    ...columns,
                ]);
            }
        },
    );

    useEffect(() => {
        if (isFunction(onFilter)) {
            onFilter(filters);
        }
    }, [filters]);

    useEffect(() => {
        if (enableRowSelection) {
            setSelectedRows(selectedFlatRows);
        }
    }, [selectedFlatRows, enableRowSelection]);

    return (
        <>
            <div className="sticky-table" style={wrapperStyles}>
                <Table hover {...getTableProps()} className="sticky-table">
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => {
                                    return (
                                        <th
                                            {...column.getHeaderProps([
                                                { style: column.headerStyle },
                                            ])}
                                        >
                                            <div
                                                className="d-flex align-items-center"
                                                {...column.getHeaderProps([
                                                    {
                                                        className:
                                                            column.className,
                                                        style: column.style,
                                                    },
                                                ])}
                                            >
                                                {column.render('Header')}
                                                {/* Render the columns filter UI */}

                                                {column.canFilter ? (
                                                    <div>
                                                        {column.render(
                                                            'Filter',
                                                        )}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row);
                            const rowProps = row.getRowProps();
                            const trActions = isSubComponent
                                ? { ...row.getToggleRowExpandedProps() }
                                : '';

                            return (
                                // Use a React.Fragment here so the table markup is still valid
                                <React.Fragment key={rowProps.key}>
                                    <tr
                                        {...trActions}
                                        {...row.getRowProps(
                                            formatRowProps &&
                                                formatRowProps(row),
                                        )}
                                        className={
                                            row.isExpanded ? 'expanded' : ''
                                        }
                                    >
                                        {row.cells.map((cell) => {
                                            return (
                                                <td
                                                    {...cell.getCellProps([
                                                        {
                                                            className:
                                                                cell.column
                                                                    .className,
                                                        },
                                                    ])}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>

                                    {row.isExpanded ? (
                                        <tr className="expanded">
                                            <td colSpan={visibleColumns.length}>
                                                {renderRowSubComponent({
                                                    row,
                                                    rowProps,
                                                    visibleColumns,
                                                })}
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>

                {isLoading && <Loading />}
            </div>

            <ReactPaginate
                previousLabel="Previous"
                nextLabel="Next"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                breakLabel="..."
                breakClassName="page-item"
                breakLinkClassName="page-link"
                pageCount={pageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={({ selected }) => {
                    // gotoPage(selected);
                    handleSearch('page', selected);
                }}
                containerClassName="pagination"
                activeClassName="active"
                forcePage={!!pageCount ? currentPage : -1}
            />
        </>
    );
};

export default StickyTable;
